import React, { useMemo } from 'react';
import { useReportConfiguration } from 'components/reporting/sidebar/ConfiguredReport';
import { isNil, flatMap } from 'lodash';
import { BasicTooltip, useTooltip } from '@nivo/tooltip';
import { getColor, round } from '../helpers';

function LinearTrendline({ data, xScale, yScale, margin }) {
  const { get, meta } = useReportConfiguration();
  const useLinearTrendline = get('trendline') === 'linear';

  function getValueIfPresent(key) {
    const value = get(key);

    if (isNil(value) || value === '') {
      return undefined;
    } else {
      return value;
    }
  }

  const minXConfig = getValueIfPresent('horizontalMinValue');
  const maxXConfig = getValueIfPresent('horizontalMaxValue');
  const minYConfig = getValueIfPresent('MinValue');
  const maxYConfig = getValueIfPresent('MaxValue');

  const { slope, intercept, startPoint, endPoint } = useMemo(() => {
    if (!useLinearTrendline) return {};

    const points = flatMap(data, dataSeries => dataSeries.data);
    const n = points.length;
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumXX = 0;
    let sumYY = 0;

    let [minX, maxX, minY, maxY] = [
      minXConfig,
      maxXConfig,
      minYConfig,
      maxYConfig,
    ];

    points.forEach(({ x, y }) => {
      if (x < minX || isNil(minX)) minX = x;
      if (x > maxX || isNil(maxX)) maxX = x;
      if (y < minY || isNil(minY)) minY = y;
      if (y > maxY || isNil(maxY)) maxY = y;

      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumXX += x * x;
      sumYY += y * y;
    });

    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    const r2 = Math.pow(
      (n * sumXY - sumX * sumY) /
        Math.sqrt((n * sumXX - sumX * sumX) * (n * sumYY - sumY * sumY)),
      2
    );

    const startPoint = {
      x: minX,
      y: slope * (minX || 0) + intercept,
    };
    const endPoint = {
      x: maxX,
      y: slope * (maxX || 0) + intercept,
    };

    return { slope, intercept, r2, startPoint, endPoint };
  }, [
    data,
    minXConfig,
    maxXConfig,
    minYConfig,
    maxYConfig,
    useLinearTrendline,
  ]);

  const { showTooltipFromEvent, hideTooltip } = useTooltip();
  const tooltip = (
    <BasicTooltip
      id={meta.reportColumnNames.y}
      value={`${round(slope, 3)} * ${meta.reportColumnNames.x} + ${round(
        intercept,
        3
      )}`}
    />
  );

  function handleTooltip(show) {
    if (show) {
      return event => {
        showTooltipFromEvent(tooltip, event);
      };
    } else {
      return () => hideTooltip();
    }
  }

  if (useLinearTrendline) {
    return (
      <>
        <line
          x1={xScale(startPoint.x)}
          y1={yScale(startPoint.y)}
          x2={xScale(endPoint.x)}
          y2={yScale(endPoint.y)}
          stroke={getColor(get(), meta, '')}
          strokeWidth='1'
        />
        <line
          onMouseEnter={handleTooltip(true)}
          onMouseMove={handleTooltip(true)}
          onMouseLeave={handleTooltip(false)}
          x1={xScale(startPoint.x)}
          y1={yScale(startPoint.y)}
          x2={xScale(endPoint.x)}
          y2={yScale(endPoint.y)}
          stroke='transparent'
          strokeWidth='30'
        />
      </>
    );
  } else {
    return null;
  }
}

export default LinearTrendline;

// WIP for determining approximate point based on hover. Might not make it to
// prod, but I'm leaving for now in case we decide to do this.
// function axisValuesFromMouseEvent(event) {
//   const svg = event.target.closest('svg');
//   const pt = svg.createSVGPoint();
//   const bb = svg.getBoundingClientRect();
//   pt.x = event.clientX - margin.left;
//   pt.y = event.clientY - margin.top - bb.top;
//   const { x, y } = pt.matrixTransform(svg.getScreenCTM().inverse());

//   console.debug({
//     x: (x / props.innerWidth) * (maxX - minX) + minX,
//     y: (y / props.innerHeight) * (maxY - minY) + minY,
//   });
// }
